// 21 grams
import {LoanListItem} from '../data.service'
import {Service, signal} from '@angular/core'
import {DateTime} from 'luxon'
import {ILoanListItem} from '@sparbanken-syd/renewal-tool'

export const LOAN_EXCLUDED = 24 * 60 * 60 * 1000 * 21

@Service()
export class LetterSelector {

  public hagelDate = signal<string>('')

  private readonly threeMonth = ['Borgo', 'l751', 'l752', 'l753', 'l754', 'l756']

  public loanIsExcluded(loan: Pick<LoanListItem, 'ex'>): boolean {
    if (loan.ex) {
      return loan.ex >= Date.now()
    }
    return false
  }

  /**
   * Check if Omsättningbrev have been sent for this loan
   *
   */
  public obSent(item: Pick<LoanListItem, 'ob' | 'vb' | 'productCode'>): boolean {
    // If never set it is included
    if (!item.ob) {
      return false
    }
    // If it is a Borgo simply check if last ob was more than two months ago.
    if (item.productCode === 'Borgo') {
      return (item.ob + 60 * 60 * 24 * 60 * 1000) >= Date.now()
    }
    // If sent within last two months, then consider it sent.
    const fwd = DateTime.now().minus({month: 2}).toMillis()
    if (item.ob > fwd) {
      return true
    }
    // If VB never sent then it cannot have been sent, note that we make the
    // Assertion no ob above.
    if (!item.vb) {
      return false
    }
    // If the Oms. has been sent _after_ the vb, then it is considered sent
    return item.ob > item.vb
  }

  public discountDeadline(item: Pick<LoanListItem, 'v'>): number {
    // If no villkorsändringsdag, then we set it to one year from now.
    // rare
    if (!item.v) {
      return DateTime.now().plus({month: 12}).toMillis()
    }

    /**
     * If not set, and we can calculate it, is 75 days from V.
     */
    return DateTime.fromMillis(item.v).minus({days: 45}).toMillis()
  }

  public valueOrMissing(item: Partial<ILoanListItem>, key: keyof ILoanListItem): string {
    return item[key] as string ?? 'Saknas'
  }

  public filterForDiscount(loans: LoanListItem[]): LoanListItem[] {
    return loans.filter(loan => {
      // If no Villkorsändringsdag and no deadline do not include the loan
      if (!loan.renewalDate) {
        return false
      }
      return loan.renewalDate > Date.now() &&
        loan.renewalDate < DateTime.now().plus({day: 75}).toMillis() && this.isReboundLoan(loan)
    })
  }

  /**
   * If no discount set on BOTH ds and dl then it is missing
   * In the future we will add a timeStamp here.
   */
  public missingDiscount(l: LoanListItem): boolean {
    return l.ds === undefined || !Array.isArray(l.db)
  }

  /**
   * Loans that have a discount today
   */
  public haveDiscount(l: LoanListItem, have: boolean | null): boolean {
    if (have === null) {
      return true
    }

    if (have) {
      return !!l.interestDeviation
    } else {
      return !l.interestDeviation
    }
  }

  /**
   * Only some loans get omsättningsbrev and potentially villkorsbilaga
   * That is Borgo and l753-4 + l756 always get. But other loans only when
   * they have fixed terms. (1 år - n år)
   */
  public isReboundLoan(loan: Pick<LoanListItem, 'productCode' | 'frequency'>): boolean {
    // These have rebound always
    const rebounds = new Set(['Borgo', 'l751', 'l752', 'l753', 'l754', 'l756'])
    if (rebounds.has(loan.productCode)) {
      return true
    }
    // Other loans have rebound only if they are "fixed" (1 år, 2 år n år)
    return !['3 må', '2 må', '1 må'].includes(loan.frequency)
  }

  public filterVB(loans: LoanListItem[]): LoanListItem[] {
    return loans.filter(l => {
      if (l.pvd === null || l.pvd === undefined) {
        return false
      }
      // Previous villkorsändringsdag has to be before today?
      // Skip all that have a date in vb. Since they have been given a vb already
      // Here we have to think about next year!
      // Skip all Borgo loans, we do not send for them
      return l.ob !== null && (l.pvd < Date.now()) && (l.pvd >= l.ob) &&
        !this.loanIsExcluded(l) &&
        // We never send for Borgo loans.
        l.productCode !== 'Borgo' &&
        // Only if Omsättningsbrev is sent, and sent within 90 days
        (l.ob !== null && l.ob > Date.now() - 60 * 60 * 24 * 90 * 1000) &&
        // Only if vb not sent OR vb sent before ob
        (l.vb === null || l.vb < l.ob)
    })
  }

  public filterOB(loans: LoanListItem[]): LoanListItem[] {

    // We look forward 2 months from "today" so in december we look at jan 1st - feb 28th
    const fwd = DateTime.now().set({day: 1}).plus({month: 3}).minus({day: 14})
    const end = DateTime.now().set({day: 1})//.plus({month: 1})

    return loans    // Only accept 3-month loans of special kind
      .filter(l => this.threeMonth.includes(l.productCode) || l.frequency.includes('år'))
      .filter(l => {
        // All loans two months into the future, none older than today and none that
        // have already received their letter.
        return l.renewalDate < fwd.toMillis() && l.renewalDate >= end.toMillis() &&
          !this.obSent(l) && !this.loanIsExcluded(l)
      })
  }
}
